/**
 * Predicado puro (#538 — GenIA de Intake, Correção 2 do stop-gate rodada 2)
 * que decide se a lista de agentes INTERNOS (hoje só o `genia-intake`) deve
 * ser buscada e mesclada à lista de agentes do chat.
 *
 * Extraído de `ChatWidget.vue` (onde vivia como `if (this.isManagement)`
 * dentro de `loadAgents()`, ligado ao watcher de `isManagement`) porque a
 * decisão cruza dois fatos frágeis, difíceis de testar sem montar o
 * componente Vue:
 *
 *  1. A restauração de sessão é ASSÍNCRONA (Codex, #538 v2): `App.vue`
 *     monta o `ChatWidget` assim que há token no localStorage
 *     (`v-if="isAuthenticated"`), mas `state.auth.user` só é preenchido
 *     com o e-mail real quando a action `SESSION_RESTORE` resolve. Até lá,
 *     `state.auth.user` é `{}` (valor default do store, `email` ausente) —
 *     NÃO significa "não é Admin", significa "ainda não sabemos quem é".
 *     Tratar os dois casos como iguais faz o Admin abrir o chat antes da
 *     restauração terminar e nunca ver o `genia-intake` naquela sessão.
 *  2. O widget não pode reemitir a busca indefinidamente toda vez que o
 *     estado é reavaliado (watcher de `isManagement` disparando de novo) —
 *     por isso o segundo parâmetro (`jaCarregou`).
 *
 * Nenhuma dependência nova: função pura, sem Vue, sem store, sem DOM —
 * testável em Node puro com o runner de testes já usado no projeto
 * (mocha, `npm test`).
 *
 * @param {{ email?: string } | null | undefined} user - o objeto de usuário
 *   do store de auth (`state.auth.user` / getter `sessionUser`). `null`,
 *   `undefined` ou um objeto sem `email` (o `{}` inicial do store) contam
 *   como "sessão ainda não restaurada" — diferente de "restaurada e não é
 *   Admin".
 * @param {boolean} jaCarregou - `true` se a lista de agentes internos já
 *   foi carregada nesta sessão do widget. Impede recarregar em loop mesmo
 *   que o predicado seja chamado de novo (ex.: watcher disparando outra
 *   vez `loadAgents()`).
 * @returns {boolean} `true` somente quando a sessão já foi restaurada, o
 *   usuário é o Admin (`admin@admin.com`, e-mail único fixo do projeto —
 *   não existe papel de admin por usuário) e a lista interna ainda não foi
 *   carregada.
 */
function shouldLoadInternalAgents(user, jaCarregou) {
  if (jaCarregou) return false;
  if (!user || !user.email) return false; // sessão ainda não restaurada
  return user.email === 'admin@admin.com';
}

// Id do agente de intake — espelha `INTAKE_AGENT_ID` de
// `api/middlewares/intakeAllowlistGate.js` (não há import cross-bundle
// client/api; o valor é o mesmo slug estável usado em `agent.id`, ver
// `api/agents/genia-intake.js`).
const INTAKE_AGENT_ID = 'genia-intake';

const BASE_CHAT_FILE_ACCEPT = '.pdf,.png,.jpg,.jpeg,.docx';
const INTAKE_ONLY_FILE_ACCEPT = '.xlsx,.xls';

/**
 * Predicado puro (#564 — GenIA de Intake aceita planilha `.xlsx/.xls`) que
 * decide o valor do `accept` do input de arquivo do chat: a base de sempre,
 * mais planilha SOMENTE quando o agente selecionado é o intake. Extraído de
 * `ChatWidget.vue` pelo mesmo motivo do `shouldLoadInternalAgents` acima —
 * função pura, sem Vue/DOM, testável em Node puro com mocha.
 *
 * @param {string|null|undefined} selectedAgentId - `this.selectedAgentId`
 *   do widget (id do agente escolhido no seletor, ou `null` sem seleção).
 * @returns {string} valor pronto para o atributo `accept` do `<input
 *   type="file">`.
 */
function getChatFileAccept(selectedAgentId) {
  if (selectedAgentId === INTAKE_AGENT_ID) {
    return `${BASE_CHAT_FILE_ACCEPT},${INTAKE_ONLY_FILE_ACCEPT}`;
  }
  return BASE_CHAT_FILE_ACCEPT;
}

// CommonJS de propósito (não ESM `export`): o mocha da raiz roda em Node
// puro, sem babel/register — `require()` deste arquivo tem que funcionar
// sem transpilar. O webpack do client (vue-cli-service) interopera CJS
// normalmente, então `import { shouldLoadInternalAgents } from '...'` no
// ChatWidget.vue continua funcionando sem mudança nenhuma na config.
module.exports = {
  shouldLoadInternalAgents,
  getChatFileAccept,
  INTAKE_AGENT_ID,
  BASE_CHAT_FILE_ACCEPT,
  INTAKE_ONLY_FILE_ACCEPT,
};
